import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
} from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { MicrofrontendComponent } from '../microfrontend.component';
import { AppName, AppUrl } from '../microfrontend.tokens';
import {
  IBudgetCommonData,
  ICatalogue,
  SequenceTypes,
  TypeAmplitudeBudget,
  TypeBudget,
  TypeCategory,
} from '../../../types-legacy';
import {
  AuthService,
  FinalService,
  StorageService,
} from '@web-frontend/shared/services';
import { BudgetService } from '@web-frontend/shared/services/budgets';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalSendComponent } from '@web-frontend/shared/components/modal-send';
import { CreateFinalV2Service } from '@web-frontend/shared/components/create-final-v2';
import { CreateSequenceComponent } from '@web-frontend/shared/components/create-sequence/create-sequence.component';
import { CreateCatalogueService } from '@web-frontend/shared/components/create-catalogue';
import { Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import {
  AnalyticsService,
  Source,
} from '../../shared/services/analytics/analytics.service';
import { UpdateCompanyDataComponent } from '../../shared/components/update-company-data/update-company-data.component';
import { map } from 'rxjs/operators';
import { UpdateCompanyDataService } from '../../shared/components/update-company-data/update.company-data.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { BudgetLogoService } from '../../shared/services/budget-logo';
import { CreateDealService } from '../../shared/components/create-deal';
import { SharePaymentLinkModalService } from '../../shared/components/create-sale/share-payment-link-modal/share-payment-link-modal.service';
import { InvoiceService } from '../../invoice/invoice.service';
import Stripe from 'stripe';
import { SettingsModalComponent } from '../../shared/directives/open-settings/settings-modal.component';
import { AddCategoryCommonService } from '../../shared/components/categories-base-manager/add-categories/add-categories-common.service';
import { DEFAULT_STATUS_BY_BUDGET_TYPE } from '../../shared/utils';
import { TypeOfCRUD } from '../../shared/enums/crud.enum';
import { CreateBudgetService } from '../../shared/components/create-budget/create-budget.service';
import { FeatureFlagsService } from '../../shared/services/feature-flags/feature-flags.service';
import { NoLoginRegisterModalComponent } from '../../shared/directives/no-login-register/no-login-register-modal.component';
import { CreateProjectService } from '../../shared/components/create-project';
import { FileWriteService } from '@web-frontend/shared/helpers/file-write';
import { TutorialService } from '@web-frontend/shared/services/tutorial';
import { VideoService } from '@web-frontend/shared/components/modal-video/video.service';
import { PlatformService } from '@web-frontend/shared/components/platform-disable-content/platform.service';
import { SubscriptionService } from '@web-frontend/shared/services/subscription';
import { emitOnboardingToSubscription } from '@web-frontend/shared/events/onboarding-events';
import { SubscriptionViewService } from '@web-frontend/shared/services/subscription-view';
import { AssetDownloadService } from '@web-frontend/shared/services/assets/asset-download.service';

export enum EventType {
  openCreateFinal = 'openCreateFinal',
  openUpdateFinal = 'openUpdateFinal',
  openCreateSerial = 'openCreateSerial',
  openCreateConcept = 'openCreateConcept',
  navigate = 'navigate',
  sendEmail = 'sendEmail',
  sendAnalyticsEvent = 'sendAnalyticsEvent',
  scrollToTop = 'scrollToTop',
  updateCompanyData = 'updateCompanyData',
  openUpdateCompany = 'openUpdateCompany',
  openUploadLogo = 'openUploadLogo',
  showToast = 'showToast',
  openFinalSideModal = 'openFinalSideModal',
  openDealSideModal = 'openDealSideModal',
  chargeAndShare = 'chargeAndShare',
  openConfigurationModal = 'openConfigurationModal',
  openCreateCategory = 'openCreateCategory',
  convertToProform = 'convertToProform',
  finishCreateDocument = 'finishCreateDocument',
  openCreateProject = 'openCreateProject',
  openCreateDeal = 'openCreateDeal',
  downloadFile = 'downloadFile',
  downloadMultipleFiles = 'downloadMultipleFiles',
  showYoutubeTutorial = 'showYoutubeTutorial',
  openAcademyLink = 'openAcademyLink',
  checkBillingDocumentsQuota = 'checkBillingDocumentsQuota',
  onboardingToSubscription = 'onboardingToSubscription',
  closeFinalSideModal = 'closeFinalSideModal',
  closeDealSideModal = 'closeDealSideModal',
  openOnboardingDownloadInvoicesPlan = 'openOnboardingDownloadInvoicesPlan',
}

const amplitudeType = {
  [TypeBudget.BUDGET]: TypeAmplitudeBudget.BUDGET,
  [TypeBudget.PROFORM]: TypeAmplitudeBudget.PROFORM,
  [TypeBudget.BILL]: TypeAmplitudeBudget.BILL,
  [TypeBudget.WAL]: TypeAmplitudeBudget.WAL,
};

type EventPayload =
  | {
      videoType: TypeAmplitudeBudget;
      typeOfBudget: string;
    }
  | { resource: string }
  | { id: string; type: string }
  | { event: string }
  | {
      type: 'info' | 'success' | 'error';
      text: string;
      link?: string;
      linkData?: string;
    };

type SendEventPayload = { id: string } | { conceptName: string; index: number };

type EventData = {
  type: EventType;
  payload?: EventPayload;
};

type SendEventData = {
  type: EventType;
  payload?: SendEventPayload;
};

@Component({
  selector: 'roma-mf-documents',
  template: `
    <div>
      <no-login-register-banner></no-login-register-banner>
      <mf-documents
        [attr.sendemail]="featureFlagEmail"
        [attr.customer]="finalId"
        [attr.deal]="dealId"
      ></mf-documents>
    </div>
  `,
})
export class MfDocumentsComponent extends MicrofrontendComponent<
  EventData,
  SendEventData
> {
  @Input() appName = 'mf-documents';
  @Input() appUrl: string = localStorage.getItem('mf-documents');
  @Input() finalId: string | undefined = undefined;
  @Input() dealId: string | undefined = undefined;
  featureFlagEmail = false;
  sub$ = new Subscription();
  academyLink = '';

  constructor(
    private readonly budgetService: BudgetService,
    private readonly finalService: FinalService,
    private readonly analyticsService: AnalyticsService,
    private readonly createFinalService: CreateFinalV2Service,
    private readonly createDealService: CreateDealService,
    private readonly updateCompanyDataService: UpdateCompanyDataService,
    private readonly createCatalogue: CreateCatalogueService,
    private readonly toastService: ToastService,
    router: Router,
    private readonly budgetLogoService: BudgetLogoService,
    private readonly sharePaymentLinkModalService: SharePaymentLinkModalService,
    private readonly invoiceService: InvoiceService,
    private readonly addCategoryCommonService: AddCategoryCommonService,
    private readonly i18n: TranslateService,
    private readonly createBudgetService: CreateBudgetService,
    private readonly featureFlags: FeatureFlagsService,
    private readonly authService: AuthService,
    private readonly createProjectService: CreateProjectService,
    private readonly tutorialService: TutorialService,
    private readonly platformService: PlatformService,
    private video: VideoService,
    private dialog: MatDialog,
    private readonly fileWrite: FileWriteService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(AppName) appName: string,
    @Inject(AppUrl) appUrl: string,
    private _httpClient: HttpClient,
    private _el: ElementRef,
    private readonly subscriptionService: SubscriptionService,
    private readonly subscriptionViewService: SubscriptionViewService,
    private readonly assetDownloadService: AssetDownloadService
  ) {
    super(_httpClient, _el, router);
    this.handleCustomEvent = this.handleCustomEvent.bind(this);
  }

  isLowerThan1450px = window.innerWidth < 1450;
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isLowerThan1450px = window.innerWidth < 1450;
  }

  public refresh() {
    this.draw();
  }

  private draw() {
    this.changeDetectorRef.detectChanges();
  }

  getEmailFeatureFlag(): void {
    this.featureFlags
      .getValue('show_send_email', false)
      .then((featureFlagEmail: boolean) => {
        this.featureFlagEmail = featureFlagEmail;
      });
  }

  onInit(): void {
    this.getEmailFeatureFlag();
    this.loadAcademyLink();
  }

  handleCustomEvent(event: CustomEvent<EventData>): void {
    const { type, payload } = event.detail;
    const eventsActionsMap = {
      [EventType.sendEmail]: this.handleSendEmail,
      [EventType.openCreateFinal]: this.handleOpenCreateFinal,
      [EventType.openUpdateFinal]: this.handleOpenUpdateFinal,
      [EventType.openCreateSerial]: this.handleOpenCreateSequence,
      [EventType.sendAnalyticsEvent]: this.handleAnalyticsEvent,
      [EventType.openCreateConcept]: this.handleOpenCreateConcept,
      [EventType.navigate]: this.handleNavigate,
      [EventType.scrollToTop]: this.handleScrollToTop,
      [EventType.updateCompanyData]: this.handleUpdateCompanyData,
      [EventType.openUploadLogo]: this.handleOpenUploadLogo,
      [EventType.showToast]: this.handleShowToast,
      [EventType.openFinalSideModal]: this.handleOpenFinalSideModal,
      [EventType.openDealSideModal]: this.handleOpenDealSideModal,
      [EventType.chargeAndShare]: this.handleChargeAndShare,
      [EventType.openConfigurationModal]: this.handleOpenSettings,
      [EventType.openCreateCategory]: this.handleOpenCreateCategory,
      [EventType.convertToProform]: this.handleConvertToProform,
      [EventType.finishCreateDocument]: this.handleOpenNoLoginRegister,
      [EventType.openCreateProject]: this.handleOpenCreateProject,
      [EventType.openCreateDeal]: this.handleOpenCreateDeal,
      [EventType.downloadFile]: this.handleDownloadFile,
      [EventType.openAcademyLink]: this.handleOpenAcademyLink,
      [EventType.showYoutubeTutorial]: this.handleShowYoutubeTutorial,
      [EventType.checkBillingDocumentsQuota]: this
        .handleCheckBillingDocumentsQuota,
      [EventType.onboardingToSubscription]: emitOnboardingToSubscription,
      [EventType.closeFinalSideModal]: this.handleCloseFinalSideModal,
      [EventType.closeDealSideModal]: this.handleCloseDealSideModal,
      [EventType.openOnboardingDownloadInvoicesPlan]: this
        .handleDownoadInvoicesPlan,
      [EventType.downloadMultipleFiles]: this.handleDownloadMultipleFiles,
    };

    const action = eventsActionsMap[type];
    if (action) {
      action.call(this, payload);
    }
  }

  handleDownloadMultipleFiles({ uris }: { uris: string[] }): void {
    this.assetDownloadService.requestZip(uris);
  }

  handleDownoadInvoicesPlan(): void {
    this.subscriptionViewService.openOnboardingDownloadInvoicesPlanModal();
  }

  handleCheckBillingDocumentsQuota(): void {
    this.subscriptionService.checkBillingDocumentsQuota();
  }

  handleOpenFinalSideModal({ id }: { id: string }): void {
    this.createFinalService.open({ data: { idCustomer: id, mode: 'EDIT' } });
  }

  handleCloseFinalSideModal(): void {
    this.createFinalService.close();
  }

  handleCloseDealSideModal(): void {
    this.createDealService.close();
  }

  handleOpenDealSideModal({ id }: { id: string }): void {
    this.createDealService.open({ data: { idDeal: id, mode: 'EDIT' } });
  }

  handleToastLink(linkData?: any): void {
    if (this.platformService.isNative()) {
      this.router.navigateByUrl(linkData);
    } else {
      window.open(linkData, '_blank');
    }
  }

  handleShowToast({
    type,
    text,
    link,
    linkData,
  }: {
    type: 'info' | 'success' | 'error';
    text: string;
    link?: string;
    linkData?: string;
  }): void {
    this.toastService.show({
      text,
      type,
      link: link,
      linkData: () => this.handleToastLink(linkData),
      msDuration: 4000,
    });
  }

  handleScrollToTop(): void {
    const element = document.getElementById('bodyContainer');
    element?.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }

  async handleAnalyticsEvent({
    event,
    sources,
    properties = {},
  }: {
    event: string;
    sources: Source[];
    properties?: { [key: string]: string };
  }): Promise<void> {
    if (this.finalId || this.dealId) {
      return;
    }
    this.analyticsService.trackEvent({
      sources: sources,
      eventName: event,
      eventProperties: properties,
    });
  }

  async handleConvertToProform({
    budgetId,
  }: {
    budgetId: string;
  }): Promise<void> {
    this.router.navigateByUrl(`/admin/budgets?type=PROFM`);
    const budget = await this.budgetService.findOneBudget(budgetId).toPromise();
    const id_from = budget._id;
    const type_from = TypeBudget.PROFORM;
    budget.header.prefix = undefined;
    budget.header.numberDoc = undefined;
    delete budget._id;
    budget.status = DEFAULT_STATUS_BY_BUDGET_TYPE[TypeBudget.PROFORM].id;
    const data = {
      type: TypeBudget.PROFORM,
      mode: TypeOfCRUD.UPDATE,
      budget,
      convert: true,
      id_from,
      type_from,
    };
    this.createBudgetService
      .open(data)
      .pipe(map((res) => !res))
      .subscribe(console.log);
  }

  async handleOpenCreateSequence({
    type,
  }: {
    type: SequenceTypes;
  }): Promise<void> {
    const data = {
      name: '',
      type: type,
      incrementValue: 1,
      currentNumber: 1,
      prefix: '',
      idCompany: StorageService.CompanyId,
      disableBudgetList: true,
    };

    const event = type.toLowerCase();
    this.analyticsService.trackEvent({
      sources: ['amplitude'],
      eventName: `${event}_card_editSerie_addSerie_start`,
    });

    const dialog = this.dialog.open(CreateSequenceComponent, {
      data: data,
    });
    dialog
      .afterClosed()
      .toPromise()
      .then((id) => {
        if (id) {
          this.publish({
            type: EventType.openCreateSerial,
            payload: { id },
          });
          this.analyticsService.trackEvent({
            sources: ['amplitude'],
            eventName: `${event}_card_editSerie_addSerie_add`,
          });
        }
      });
  }

  async handleChargeAndShare({
    id,
    type,
  }: {
    id: string;
    type: TypeBudget;
  }): Promise<void> {
    const budget = await this.invoiceService.findOne(id);

    if (!budget.paymentLink?.url) {
      const paymentLink: Stripe.Response<Stripe.PaymentLink> = await this.budgetService
        .createPaymentLinkForBill(id)
        .toPromise();
      budget.paymentLink = paymentLink;
    }

    this.sharePaymentLinkModalService.open({
      data: {
        url: budget.paymentLink.url,
        idSale: budget._id,
        module: 'budgets',
        finalEmail: budget.header?.final || null,
        type: amplitudeType[type],
      },
    });
  }

  async handleOpenCreateConcept({ index }: { index: number }): Promise<void> {
    this.createCatalogue
      .open({ data: { returnData: true } })
      .toPromise()
      .then((data: ICatalogue) => {
        if (data)
          this.publish({
            type: EventType.openCreateConcept,
            payload: { conceptName: data.name, id: data._id, index },
          });
      });
  }

  handleOpenCreateProject(): void {
    this.createProjectService
      .open()
      .toPromise()
      .then((data) => {
        if (data) {
          this.publish({
            type: EventType.openCreateProject,
            payload: { id: data._id },
          });
        }
      });
  }

  handleOpenCreateDeal(): void {
    this.createDealService.open().subscribe((data) => {
      if (data) {
        this.publish({
          type: EventType.openCreateDeal,
          payload: { id: data },
        });
      }
    });
  }

  async handleUpdateCompanyData(): Promise<void> {
    const dialogRef = this.dialog.open(UpdateCompanyDataComponent, {
      autoFocus: false,
      disableClose: false,
    });

    this.sub$.add(
      dialogRef
        .afterClosed()
        .pipe(
          map((res) => {
            if (res)
              this.publish({
                type: EventType.updateCompanyData,
              });
          })
        )
        .subscribe()
    );
  }

  async handleOpenUploadLogo(): Promise<void> {
    const isDone = await this.budgetLogoService.openWithoutImage(
      JSON.parse(StorageService.budgetSettings)?._id
    );
    if (isDone) {
      this.publish({
        type: EventType.updateCompanyData,
      });
    }
  }

  async handleNavigate({
    path,
    params,
    newWindow,
  }: {
    path: string;
    params?: Params;
    newWindow?: boolean;
  }): Promise<void> {
    if (newWindow) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`admin/${path}`], { queryParams: params })
      );
      window.open(url, '_blank');
    } else {
      this.router.navigate([`admin/${path}`], { queryParams: params });
    }
  }

  async handleOpenCreateFinal({ type }: { type: string }): Promise<void> {
    this.analyticsService.trackEvent({
      sources: ['amplitude'],
      eventName: `${type}_card_createFinal_start`,
    });
    this.createFinalService
      .openCreatePartially()
      .toPromise()
      .then((data) => {
        if (data)
          this.publish({
            type: EventType.openCreateFinal,
            payload: { id: data },
          });
      });
  }

  async handleOpenUpdateFinal({
    id,
    type,
  }: {
    id: string;
    type: string;
  }): Promise<void> {
    this.analyticsService.trackEvent({
      sources: ['amplitude'],
      eventName: `${type}_card_editFinal_start`,
    });
    this.createFinalService
      .openUpdatePartially(id)
      .toPromise()
      .then((data) => {
        if (data)
          this.publish({
            type: EventType.openUpdateFinal,
            payload: { id: id },
          });
      });
  }

  async handleOpenCreateCategory(): Promise<void> {
    this.addCategoryCommonService
      .open({ data: { type: 'ADD', typeCategory: TypeCategory.SALES } })
      .toPromise()
      .then((result) => {
        if (!result) return;
        this.analyticsService.trackEvent({
          sources: ['amplitude'],
          eventName: 'category_created',
        });
        const message = `customProperties.createCategoryMessage`;
        this.toastService.show({
          text: this.i18n.instant(message),
          type: 'success',
          msDuration: 4000,
        });
        this.publish({
          type: EventType.openCreateCategory,
          payload: { id: result._id },
        });
      });
  }

  async handleOpenUpdateCompany(): Promise<void> {
    this.updateCompanyDataService
      .open()
      .toPromise()
      .then((data) => {
        if (data)
          this.publish({
            type: EventType.openUpdateCompany,
          });
      });
  }

  async handleOpenSettings(): Promise<void> {
    const dialogRef = this.dialog.open(SettingsModalComponent, {
      id: 'settings-modal',
    });

    this.sub$.add(
      dialogRef.afterClosed().subscribe(() => {
        this.publish({
          type: EventType.openConfigurationModal,
        });
      })
    );
  }

  async mappingEventToEmail({
    id,
    type,
  }: {
    id: string;
    type: string;
  }): Promise<{
    type: string;
    budget: IBudgetCommonData;
    TypeAmplitudeBudget: string;
    user: { _id: string; company: { _id: string } };
    settings: { [key: string]: string };
    email: string[];
  }> {
    const typeMap = {
      [TypeBudget.BILL]: this.budgetService.findOneBill(id),
      [TypeBudget.PROFORM]: this.budgetService.findOneProform(id),
      [TypeBudget.WAL]: this.budgetService.findOneWaybill(id),
      [TypeBudget.BDG]: this.budgetService.findOneBudget(id),
    };

    const document: IBudgetCommonData = await typeMap[type].toPromise();
    const data = {
      type,
      budget: document,
      TypeAmplitudeBudget: type.toLowerCase() as TypeAmplitudeBudget,
      user: {
        _id: StorageService.UserId,
        company: { _id: StorageService.CompanyId },
      },
      email: [],
      settings: {},
    };

    const companyId = StorageService.CompanyId;

    try {
      const { allSettings, emailArr } = await forkJoin({
        allSettings: this.budgetService.findSettingsByCompany(companyId),
        emailArr: this.finalService.findOne(document.header.contact_id),
      }).toPromise();

      data.email = emailArr.emailArr;
      data.settings = allSettings;

      return data;
    } catch (error) {
      console.error('Error mapping event to email', error);
      return data;
    }
  }

  async handleSendEmail(params: { id: string; type: string }): Promise<void> {
    if (!params.id || !params.type) return;
    const data = await this.mappingEventToEmail(params);

    const config: MatDialogConfig = new MatDialogConfig();
    config.panelClass = 'send-document-modal';
    config.data = data;

    this.dialog.open(ModalSendComponent, config);

    const typeMap = {
      [TypeBudget.BILL]: 'bill',
      [TypeBudget.PROFORM]: 'proforma',
      [TypeBudget.WAL]: 'waybill',
      [TypeBudget.BDG]: 'budget',
    };

    this.analyticsService.trackEvent({
      sources: ['amplitude'],
      eventName: `${typeMap[params.type]}_view_emailSend`,
    });
  }

  handleOpenNoLoginRegister(params: {
    type: string;
    isContinue: boolean;
  }): void {
    this.sub$.add(
      this.authService.isNoLoginUser$.subscribe((isNoLogin) => {
        if (isNoLogin) {
          const dialogRef = this.dialog.open(NoLoginRegisterModalComponent, {
            id: 'no-login-register-modal-mf-documents',
          });

          dialogRef.afterClosed().subscribe(() => {
            if (!params.isContinue) {
              this.router.navigate(['/admin/budgets'], {
                queryParams: { type: params.type },
              });
            }
          });
        }
      })
    );
  }

  async handleDownloadFile(payload: {
    data: { url: string; path: string };
  }): Promise<void> {
    const { url, path } = payload.data;
    const responseBlob = await fetch(url);
    const blob = await responseBlob.blob();
    this.fileWrite.writeFile({ path, blob });
  }

  onDestroy(): void {
    this.sub$.unsubscribe();
  }

  private async loadAcademyLink(): Promise<void> {
    try {
      const invoice = await this.tutorialService.get('bills').toPromise();
      this.academyLink = invoice?.academy;
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }

  handleOpenAcademyLink(): void {
    if (!this.academyLink) return;
    window.open(`${this.academyLink}`, '_blank');
  }

  handleShowYoutubeTutorial(): void {
    this.video.open('bills');
  }
}
